'use client';

import React from 'react';
import styled from 'styled-components';

export interface MapLayoutProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Sidebar column width in px. Default 360. */
  sidebarWidth?: number;
  children: React.ReactNode;
}

export interface MapLayoutCanvasProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
}

export interface MapLayoutSidebarProps extends React.HTMLAttributes<HTMLElement> {
  children?: React.ReactNode;
}

const CANVAS_SLOT = Symbol.for('liftango.map-layout.canvas');
const SIDEBAR_SLOT = Symbol.for('liftango.map-layout.sidebar');

interface SlotComponent<P> extends React.FC<P> {
  __slot: symbol;
}

function createSlot<P>(name: string, slot: symbol, Component: React.FC<P>): SlotComponent<P> {
  const fn = Component as SlotComponent<P>;
  fn.__slot = slot;
  fn.displayName = name;
  return fn;
}

const Root = styled.div<{ $hasSidebar: boolean; $sidebarWidth: number }>`
  box-sizing: border-box;
  display: grid;
  grid-template-columns: ${(p) =>
    p.$hasSidebar ? `minmax(0, 1fr) ${p.$sidebarWidth}px` : 'minmax(0, 1fr)'};
  gap: ${(p) => p.theme.spaceAlias.md};
  width: 100%;
  height: 100%;
  min-height: 0;
  align-items: stretch;
`;

const Canvas = styled.div`
  box-sizing: border-box;
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 0;
  background: ${(p) => p.theme.colors.neutral.panel};
  border: 1px solid ${(p) => p.theme.colors.neutral.line};
  border-radius: ${(p) => p.theme.radius.md};
  overflow: hidden;
`;

const Sidebar = styled.aside`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const CanvasComponent = createSlot<MapLayoutCanvasProps>(
  'MapLayout.Canvas',
  CANVAS_SLOT,
  ({ children, ...rest }) => <Canvas {...rest}>{children}</Canvas>,
);

const SidebarComponent = createSlot<MapLayoutSidebarProps>(
  'MapLayout.Sidebar',
  SIDEBAR_SLOT,
  ({ children, ...rest }) => <Sidebar {...rest}>{children}</Sidebar>,
);

interface MapLayoutFC extends React.FC<MapLayoutProps> {
  Canvas: typeof CanvasComponent;
  Sidebar: typeof SidebarComponent;
}

/**
 * Generic 2-column layout shell for any map view: a positioned canvas
 * that hosts the actual map (Google, Mapbox, raster, schematic SVG…)
 * plus absolutely-positioned overlays, and an optional fixed-width
 * sidebar. The shell stays provider-agnostic — the consumer brings the
 * map.
 */
const MapLayoutImpl: MapLayoutFC = ({ sidebarWidth = 360, children, ...rest }: MapLayoutProps) => {
  let canvasNode: React.ReactNode = null;
  let sidebarNode: React.ReactNode = null;

  React.Children.forEach(children, (child) => {
    if (!React.isValidElement(child)) return;
    const slot = (child.type as SlotComponent<unknown> | undefined)?.__slot;
    if (slot === CANVAS_SLOT) canvasNode = child;
    else if (slot === SIDEBAR_SLOT) sidebarNode = child;
  });

  return (
    <Root $hasSidebar={!!sidebarNode} $sidebarWidth={sidebarWidth} {...rest}>
      {canvasNode}
      {sidebarNode}
    </Root>
  );
};

MapLayoutImpl.Canvas = CanvasComponent;
MapLayoutImpl.Sidebar = SidebarComponent;

export const MapLayout = MapLayoutImpl;
